import { FormEvent, useState } from "react";
import logo from "../assets/logo.png";
import { Footer } from "../components/Footer";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ApiError } from "../api/client";
import { selectNeedsTenant, useAuthStore } from "../stores/auth";

export function LoginPage() {
  const login = useAuthStore((s) => s.login);
  const navigate = useNavigate();
  const location = useLocation();
  const from =
    (location.state as { from?: { pathname?: string } } | null)?.from
      ?.pathname ?? "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await login({ email, password });
      const needsTenant = selectNeedsTenant(useAuthStore.getState());
      navigate(needsTenant ? "/select-tenant" : from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail);
      else setError("Impossible de se connecter");
    } finally {
      setPending(false);
    }
  }

  const { t, i18n } = useTranslation();

  return (
    <AuthShell title={t("loginPage.title")} subtitle="HInvoice">
      {/* Sélecteur langue même sur la page de login */}
      <div className="flex justify-end mb-4">
        <button
          type="button"
          onClick={() => i18n.changeLanguage(i18n.language === "fr" ? "en" : "fr")}
          className="flex items-center gap-1.5 px-2 py-1 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs font-medium"
        >
          <span className="text-base leading-none">{i18n.language === "fr" ? "🇫🇷" : "🇬🇧"}</span>
          <span>{i18n.language === "fr" ? "FR" : "EN"}</span>
        </button>
      </div>
      <form onSubmit={onSubmit} className="space-y-4" aria-label="login-form">
        <Field label={t("loginPage.email")} htmlFor="email">
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input"
          />
        </Field>
        <Field label={t("loginPage.password")} htmlFor="password">
          <input
            id="password"
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input"
          />
        </Field>
        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}
        <button type="submit" disabled={pending} className="btn-primary w-full">
          {pending ? t("loginPage.loading") : t("loginPage.submit")}
        </button>
        <p className="text-sm text-slate-500 text-center">
          {t("loginPage.noAccount")}{" "}
          <Link to="/signup" className="text-brand-600 hover:underline">
            {t("loginPage.signup")}
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}

// -- Helpers internes reusés par SignupPage -----------------------------------

export function AuthShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <main className="flex-1 flex items-center justify-center p-6">
        <section className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          <header className="mb-6 flex flex-col items-center">
            <img src={logo} alt="HInvoice" className="h-16 w-auto mb-4" />
            <p className="text-xs uppercase tracking-wider text-brand-600 font-semibold">
              {subtitle}
            </p>
            <h1 className="text-2xl font-bold text-slate-900 mt-1">{title}</h1>
          </header>
          {children}
        </section>
      </main>
      <Footer />
    </div>
  );
}

export function Field({
  label,
  htmlFor,
  children,
  hint,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="block text-sm font-medium text-slate-700 mb-1"
      >
        {label}
      </label>
      {children}
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}
